import { Mail, MessageCircle, Phone, Paperclip, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { channelMeta } from "@/lib/format";

const ICONS = { email: Mail, whatsapp: MessageCircle, call: Phone };

// Aug 2026 fix — an attachment on a message (e.g. a customer-emailed PDF
// filed as a real Document via the inbound intake pipeline) was always a
// plain, non-clickable label here, even though the same
// Composer.jsx-attached case already tracks attachmentDocumentIds. Sales
// had no way to actually open a document a customer sent by email. Rather
// than depend on every message-landing call site remembering to attach
// attachmentDocumentIds (several don't), this matches by name + customer,
// which works for every already-landed message too, not just future ones.
function _matchDocument(documents, message, filename) {
  if (!documents || !documents.length) return null;
  return documents.find((d) => d.name === filename && (!message.customerId || d.customerId === message.customerId)) || null;
}

export function CommunicationTimeline({ threads, documents = [], onPreviewDocument }) {
  return (
    <div className="space-y-3">
      {threads.map((m) => {
        const Icon = ICONS[m.channel];
        const meta = channelMeta[m.channel];
        const inbound = m.dir === "in";
        const delivery = String(m.deliveryStatus || "").toUpperCase();
        const outboundLabel = delivery === "RECORDED" ? "Recorded only"
          : delivery === "ACCEPTED" ? "Provider accepted"
            : delivery === "DELIVERED" ? "Delivered"
              : delivery === "READ" ? "Read"
                : delivery === "FAILED" ? "Failed"
                  : delivery === "PENDING" ? "Sending"
                    : "Sent";
        const deliveryColor = delivery === "FAILED" ? "text-rose-600"
          : delivery === "RECORDED" ? "text-amber-600"
            : delivery === "DELIVERED" || delivery === "READ" ? "text-emerald-600" : "text-slate-500";
        // Item 4 — unread state: inbound messages default to unread
        // (readAt: null) until opened (see POST /messages/{id}/read).
        // Outbound/legacy messages never show the dot.
        const unread = m.dir === "in" && !m.readAt;
        return (
          <div
            key={m.id}
            data-testid={`comm-${m.id}`}
            className={`rounded-lg border ${unread ? "border-indigo-200" : "border-slate-200"} bg-white border-l-4 ${meta.bar} p-3.5 shadow-sm animate-fade-slide`}
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                {unread && <span className="h-2 w-2 rounded-full bg-indigo-600 shrink-0" data-testid={`comm-unread-${m.id}`} title="Unread" />}
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.chip}`}>
                  <Icon className="h-3 w-3" strokeWidth={2} />
                  {meta.label}
                </span>
                <span className="text-sm font-semibold text-slate-800 truncate">{m.who}</span>
                <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${inbound ? "text-emerald-600" : deliveryColor}`}>
                  {inbound ? <ArrowDownLeft className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
                  {inbound ? "Received" : outboundLabel}
                </span>
              </div>
              <span className="text-[11px] text-slate-400 whitespace-nowrap font-mono-num">{m.when}</span>
            </div>
            {m.subject && <div className="text-sm font-medium text-slate-700 mb-0.5">{m.subject}</div>}
            <div className="text-sm text-slate-600 leading-relaxed">{m.body}</div>
            {m.outcome && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                Outcome: {m.outcome} {m.duration && `· ${m.duration}`}
              </div>
            )}
            {delivery === "FAILED" && m.failureReason && (
              <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700">
                Delivery failed: {m.failureReason}
              </div>
            )}
            {m.channel === "call" && (!m.attachments || m.attachments.length === 0) && (
              <div className="mt-2 text-[11px] text-slate-400 italic">Recording unavailable</div>
            )}
            {m.attachments && m.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                {m.attachments.map((a) => {
                  if (m.channel === "call" && /^https?:\/\//.test(a)) {
                    return <audio key={a} controls preload="none" src={a} className="h-8 max-w-full" data-testid={`comm-recording-${m.id}`} />;
                  }
                  const matchedDoc = _matchDocument(documents, m, a);
                  if (matchedDoc && onPreviewDocument) {
                    return (
                      <button key={a} type="button" onClick={(e) => { e.stopPropagation(); onPreviewDocument(matchedDoc); }}
                        data-testid={`comm-attachment-${m.id}`}
                        className="inline-flex items-center gap-1 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 hover:bg-indigo-100 transition-colors">
                        <Paperclip className="h-3 w-3" /> {a}
                      </button>
                    );
                  }
                  return (
                    <span key={a} className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded px-2 py-0.5">
                      <Paperclip className="h-3 w-3" /> {a}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
